import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { GridRound, TreasureGridState } from '../../../interfaces/treasure-grid.interface';
import { TreasureGridStageComponent } from './treasure-grid-stage.component';

/**
 * Host shell for Treasure Grid. Reads `TreasureGridState`, shows the target
 * ordered pair with the x-value tinted cyan (across) and the y-value coral
 * (up) to reinforce which number is which axis, and drives the stage's DIG
 * via @ViewChild. Outcomes are written with `scoreTreasureGridRound` +
 * `advanceTreasureGridRound`.
 */
@Component({
  selector: 'app-treasure-grid-game',
  templateUrl: './treasure-grid-game.component.html',
  styleUrls: ['./treasure-grid-game.component.scss']
})
export class TreasureGridGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  @ViewChild(TreasureGridStageComponent) stage?: TreasureGridStageComponent;

  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  showSuccess = false;
  resolving = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.showSuccess = false;
          this.resolving = false;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): TreasureGridState | null {
    return (this.session?.gameState as TreasureGridState) ?? null;
  }
  get round(): GridRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }
  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }
  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Controls ───────────────────────────────────────────────────────

  onDig(): void {
    if (this.resolving || !this.stage?.canDig) return;
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.dig();
  }

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scoreTreasureGridRound(true);
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceTreasureGridRound(), 3000);
  }

  onMissed(): void {
    if (this.resolving) return;
    // Wrong dig — buzz and let them re-plot. Nothing revealed.
    this.gameAudioService.play('assets/game-mode/sound-effects/buzzerSoundEffect.mp3');
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreTreasureGridRound(false);
    this.later(() => this.miniGameService.advanceTreasureGridRound(), 250);
  }
}
